import { Box } from '@mui/material'
import { Datagrid, FunctionField, List, SearchInput, SelectInput, useTranslate } from 'react-admin'

import type { ScaleRecord } from '../../data/source'
import { formatDateTime } from '../../domain/dateTime'
import { ManualScaleBadge } from '../atoms/ManualScaleBadge'
import { ScaleEventMarkers } from '../molecules/ScaleEventMarkers'

/**
 * Every column but Eventos shrinks to its content (width 1% + nowrap is how a
 * table cell asks for "as narrow as possible"); Eventos takes what is left.
 */
const COMPACT_COLUMNS = ['currentStartTime', 'scaleId', 'vessel', 'state'].map(source => `& .column-${source}`).join(', ')

export const ScaleList = () => {
  const translate = useTranslate()
  // Archive state as text: React Admin keeps filter values in the URL.
  const finishedChoices = [
    { id: 'false', name: translate('resources.scales.filters.active') },
    { id: 'true', name: translate('resources.scales.filters.finished') },
  ]
  const filters = [
    <SearchInput key="q" source="q" placeholder={translate('resources.scales.filters.q')} alwaysOn />,
    <SelectInput
      key="finished"
      source="finished"
      label="resources.scales.fields.finished"
      choices={finishedChoices}
      emptyText="resources.scales.filters.all"
      alwaysOn
    />,
  ]

  return (
    <List
      filters={filters}
      filterDefaultValues={{ finished: 'false' }}
      sort={{ field: 'scaleId', order: 'ASC' }}
      perPage={50}
      // Scales come from SÒSTRAT through bombeo-processor: no actions here.
      actions={false}
    >
      <Datagrid bulkActionButtons={false} rowClick="show" sx={{ [COMPACT_COLUMNS]: { width: '1%', whiteSpace: 'nowrap' } }}>
        {/* Inicio: the start of the current place event. */}
        <FunctionField<ScaleRecord>
          source="currentStartTime"
          label="resources.scales.fields.currentStartTime"
          render={record => (record.currentStartTime ? formatDateTime(record.currentStartTime) : '—')}
        />
        <FunctionField<ScaleRecord>
          source="scaleId"
          label="resources.scales.fields.scaleId"
          render={record => (
            <Box display="flex" alignItems="center" gap={1}>
              <strong>{record.scaleId}</strong>
              {record.isManual && <ManualScaleBadge seenInSource={record.seenInSource} />}
            </Box>
          )}
        />
        <FunctionField<ScaleRecord> source="vessel" label="resources.scales.fields.vessel" render={record => record.vessel ?? '—'} />
        <FunctionField<ScaleRecord>
          source="state"
          sortable={false}
          label="resources.scales.fields.state"
          render={record => translate(`enums.ScaleState.${record.state}`)}
        />
        {/* A circle per berth stay and per MARPOL/BOMBEO, in time order (see `eventMarkers`). */}
        <FunctionField<ScaleRecord>
          source="eventMarkers"
          sortable={false}
          label="resources.scales.fields.eventMarkers"
          render={record => <ScaleEventMarkers markers={record.eventMarkers} />}
        />
      </Datagrid>
    </List>
  )
}
